import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { css, keyframes } from '@emotion/css';

import {
  AccionTexto,
  Tarjeta,
  VacioCompacto,
} from '#components/mobile/inicio/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import {
  estilos,
  sinMovimiento,
} from '#components/mobile/inicio/inicio.estilos';
import type { MesCubierto } from '#components/mobile/inicio/mesesCubiertos';
import { useMesesCubiertos } from '#components/mobile/inicio/widgets/useMesesCubiertos';
import { Boton } from '#components/mobile/ui/Boton';
import { color, num, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLanguage, useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

/** Tanques que caben a lo ancho; con más, la fila se desliza. */
const TANQUES_A_LO_ANCHO = 6;
const ANCHO_TANQUE_DESLIZANTE = 56;

/** Verde más oscuro del fondo del agua (el acento mezclado con negro). */
const AGUA_FONDO = `color-mix(in srgb, ${color.ok} 78%, black)`;

const subir = keyframes({
  from: { transform: 'scaleY(0)' },
  to: { transform: 'scaleY(1)' },
});
const olaIda = keyframes({
  from: { transform: 'translateX(0)' },
  to: { transform: 'translateX(-50%)' },
});
const olaVuelta = keyframes({
  from: { transform: 'translateX(-50%)' },
  to: { transform: 'translateX(0)' },
});
const destello = keyframes({
  '0%, 100%': { opacity: 0.35 },
  '50%': { opacity: 1 },
});

const claseAgua = css({
  transformOrigin: 'bottom',
  animation: `${subir} 1.6s cubic-bezier(.3,.7,.3,1) both`,
  [sinMovimiento]: { animation: 'none' },
});
const claseOla = css({
  animation: `${olaIda} 3.2s linear infinite`,
  [sinMovimiento]: { animation: 'none' },
});
const claseOlaAtras = css({
  animation: `${olaVuelta} 5s linear infinite`,
  [sinMovimiento]: { animation: 'none' },
});
const claseLuz = css({
  animation: `${destello} 2.4s ease-in-out infinite`,
  [sinMovimiento]: { animation: 'none', opacity: 0.6 },
});
const claseFilaDeslizante = css({
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

/**
 * Meses cubiertos por adelantado (maqueta B «Las mareas», elegida por Vadik
 * el 12-oct-2026): un tanque por mes que se llena de agua con olas según lo
 * asignado frente a lo asignado este mes. Arriba, cuántos meses seguidos hay
 * cubiertos y hasta qué día; abajo, lo que falta para llenar el siguiente.
 *
 * Tamaños: compacto = cifra y tanques bajos; normal = + lo que falta y
 * «Asignar»; grande = tanques más altos.
 */
export function MesesCubiertos({ tamano, month }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const idioma = useLanguage();
  const navigate = useNavigate();
  const { datos, cargando } = useMesesCubiertos(month);

  const titulo = t('Months covered');
  const verPlan = (
    <AccionTexto onPress={() => void navigate('/budget')}>
      <Trans>See plan</Trans>
    </AccionTexto>
  );

  if (cargando) {
    return null;
  }

  if (datos.necesidad <= 0) {
    return (
      <Tarjeta titulo={titulo} sinTarjeta data-testid="inicio-meses-cubiertos">
        <VacioCompacto
          texto={t('Assign this month to see how many months you are ahead.')}
          accion={t('Assign')}
          onAccion={() => void navigate(`/asignar?month=${month}`)}
        />
      </Tarjeta>
    );
  }

  const cifra = datos.meses.toLocaleString(idioma, {
    maximumFractionDigits: 1,
  });
  const nombreMes = (mes: string) => monthUtils.format(mes, 'MMMM', locale);
  const siguiente = datos.siguiente;
  // Hay meses con dinero detrás del primero sin llenar: aún no cuentan.
  const hayDineroDespues =
    siguiente != null &&
    datos.porMes.some(m => m.mes > siguiente.mes && m.asignado > 0);
  const deslizante = datos.porMes.length > TANQUES_A_LO_ANCHO;
  const altoTanque =
    tamano === 'compacto' ? 88 : tamano === 'grande' ? 160 : 132;

  return (
    <Tarjeta
      titulo={titulo}
      accion={verPlan}
      data-testid="inicio-meses-cubiertos"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <View style={{ gap: 2, minWidth: 0 }}>
          <View
            style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6 }}
          >
            <Text
              data-testid="meses-cubiertos-cifra"
              style={{ ...texto.heroe, ...num, color: color.fg }}
            >
              {cifra}
            </Text>
            <Text style={{ ...texto.titulo, color: color.fg }}>
              {datos.meses === 1
                ? t('month of reserve')
                : t('months of reserve')}
            </Text>
          </View>
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            {datos.hasta ? (
              <>
                {t('The water reaches')}{' '}
                <strong style={{ color: color.fg }}>
                  {monthUtils.format(datos.hasta, "d 'de' MMMM", locale)}
                </strong>
              </>
            ) : (
              t('No month covered yet')
            )}
          </Text>
        </View>
        <Faro />
      </View>

      <View
        className={deslizante ? claseFilaDeslizante : undefined}
        style={{ margin: deslizante ? '0 -16px' : undefined }}
      >
        <View
          style={{
            flexDirection: 'row',
            gap: 10,
            padding: deslizante ? '0 16px' : undefined,
          }}
        >
          {datos.porMes.map((m, i) => (
            <Tanque
              key={m.mes}
              mes={m}
              indice={i}
              alto={altoTanque}
              ancho={deslizante ? ANCHO_TANQUE_DESLIZANTE : undefined}
              nombre={monthUtils.format(m.mes, 'MMM', locale)}
              importe={format(m.asignado, 'financial-no-decimals')}
            />
          ))}
        </View>
      </View>

      {tamano !== 'compacto' && siguiente && (
        <>
          <View style={{ height: 1, backgroundColor: color.line }} />
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
              <PrivacyFilter>
                <Text style={estilos.filaTitulo}>
                  {t('Fill {{month}}: {{amount}}', {
                    month: nombreMes(siguiente.mes),
                    amount: format(siguiente.falta, 'financial'),
                  })}
                </Text>
              </PrivacyFilter>
              <PrivacyFilter>
                <Text style={estilos.filaSub}>
                  {hayDineroDespues
                    ? t(
                        'Later months already have money; they count once {{month}} is full',
                        { month: nombreMes(siguiente.mes) },
                      )
                    : t('A full month is {{amount}}', {
                        amount: format(datos.necesidad, 'financial'),
                      })}
                </Text>
              </PrivacyFilter>
            </View>
            <Boton
              onPress={() => void navigate(`/asignar?month=${siguiente.mes}`)}
              style={estilos.botonFila}
            >
              <Trans>Assign</Trans>
            </Boton>
          </View>
        </>
      )}
    </Tarjeta>
  );
}

function Tanque({
  mes,
  indice,
  alto,
  ancho,
  nombre,
  importe,
}: {
  mes: MesCubierto;
  indice: number;
  alto: number;
  ancho?: number;
  nombre: string;
  importe: string;
}) {
  const lleno = mes.fraccion >= 1;
  const vacio = mes.fraccion <= 0;
  const tono = lleno ? color.ok : vacio ? color.fg2 : color.warn;
  const porcentaje = `${Math.round(mes.fraccion * 100)} %`;

  return (
    <View
      data-testid="meses-cubiertos-tanque"
      aria-label={`${nombre}: ${porcentaje}`}
      style={{
        flex: ancho ? undefined : 1,
        width: ancho,
        flexShrink: 0,
        minWidth: 0,
        alignItems: 'center',
        gap: 6,
      }}
    >
      <View
        style={{
          position: 'relative',
          width: '100%',
          height: alto,
          borderRadius: 16,
          backgroundColor: color.surface2,
          overflow: 'hidden',
          boxSizing: 'border-box',
          border: vacio ? `1.5px dashed ${color.surface3}` : undefined,
        }}
      >
        {!vacio && (
          <div
            className={claseAgua}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: `${mes.fraccion * 100}%`,
              animationDelay: `${Math.min(indice, 6) * 0.35}s`,
            }}
          >
            {!lleno && <Ola clase={claseOlaAtras} relleno={AGUA_FONDO} />}
            <Ola clase={claseOla} relleno={color.ok} arriba={lleno ? 0 : 2} />
            <div
              style={{
                position: 'absolute',
                inset: '11px 0 0 0',
                background: `linear-gradient(${color.ok}, ${AGUA_FONDO})`,
              }}
            />
          </div>
        )}
        {lleno && (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color.accentInk}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              bottom: 14,
              marginLeft: -11,
            }}
          >
            <path d="M5 12.5 10 17.5 19 7" />
          </svg>
        )}
        {!lleno && !vacio && (
          <Text
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 14,
              textAlign: 'center',
              ...texto.pequeno,
              ...num,
              color: color.warn,
            }}
          >
            {porcentaje}
          </Text>
        )}
      </View>
      <Text
        style={{
          ...texto.pequeno,
          fontWeight: 800,
          color: vacio ? color.fg2 : color.fg,
        }}
      >
        {nombre}
      </Text>
      <PrivacyFilter>
        <Text
          style={{
            fontSize: 12,
            fontWeight: 600,
            ...num,
            color: tono,
            whiteSpace: 'nowrap',
          }}
        >
          {importe}
        </Text>
      </PrivacyFilter>
    </View>
  );
}

/** Franja de olas (el doble de ancha, para deslizarla sin cortes). */
function Ola({
  clase,
  relleno,
  arriba = 0,
}: {
  clase: string;
  relleno: string;
  arriba?: number;
}) {
  return (
    <div
      className={clase}
      style={{
        position: 'absolute',
        left: 0,
        top: arriba,
        width: '200%',
        height: 12,
      }}
    >
      <svg
        width="100%"
        height="12"
        viewBox="0 0 160 12"
        preserveAspectRatio="none"
        aria-hidden="true"
        style={{ display: 'block' }}
      >
        <path
          d="M0 8 Q10 0 20 8 T40 8 T60 8 T80 8 T100 8 T120 8 T140 8 T160 8 V12 H0 Z"
          fill={relleno}
        />
      </svg>
    </div>
  );
}

/** Faro de la esquina, con la luz que parpadea. */
function Faro() {
  return (
    <svg
      width="34"
      height="40"
      viewBox="0 0 34 40"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        className={claseLuz}
        d="M17 7 L2 1 L2 13 Z M17 7 L32 1 L32 13 Z"
        fill={color.warn}
        opacity={0.6}
      />
      <rect x="13" y="3" width="8" height="7" rx="2" fill={color.warn} />
      <path d="M12 11 H22 L24 37 H10 Z" fill={color.fg} />
      <path
        d="M11.4 19 H22.6 L23 24 H11 Z M10.8 29 H23.2 L23.6 33 H10.4 Z"
        fill={color.bad}
      />
      <rect x="7" y="36" width="20" height="3" rx="1.5" fill={color.surface3} />
    </svg>
  );
}
